import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSession } from '@london-life/auth-client';
import {
  DEFAULT_PLAYER_SETTINGS,
  HOME_PLACE,
  type FlyRequest,
  boardOf,
  bringAlongOf,
  formatPence,
  hostOf,
  isLondonNight,
  mapPlaceOf,
  type NeighbourView,
  type CharacterOptionsReply,
  type MyLifeReply,
  type PlayerSettings,
  type PlayerTrainOffer,
  type PlayerWarning,
  type ProfileSetup,
  type TravelMode,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { SignInCard } from '@london-life/ui/auth/sign-in-card';
import {
  AccountCard,
  AccountHeldCard,
  AgeRestrictedCard,
  WarningCard,
  CompleteProfileCard,
  VerifyEmailCard,
} from '@london-life/ui/auth/profile-cards';
import type { ProfileValues } from '@london-life/ui/auth/profile-fields';
import PublicHomepage from './district/public-homepage';
import { LegalPage, legalDocumentAt } from './legal/legal-page';
import { LifeCreator } from './life/life-creator';
import { HouseScreen, type HouseOpen } from './house/house-screen';
import { CookieBanner, useConsent } from './consent/consent';
import { VenueScreen } from './venue/venue-screen';
import { usePlay } from './play/use-play';
import { usePeople } from './people/use-people';
import { useRealtime } from './chat/use-realtime';
import { ChatScreen } from './chat/chat-screen';
import { NoticesPanel } from './phone/notices-panel';
import type { PhoneApp } from './phone/phone-screen';
import { refreshPush, showLocalAlert } from './lib/push';
import { playChime, popupText, showMessageToast } from './phone/message-popup';
import { GameToaster } from '@london-life/ui/feedback/game-toaster';
import { notify, notifyProblem } from './lib/alerts';
import { useBackLayer } from './lib/back-stack';
import type { ChatMessage, ChatOpen, FlightView, SeatOffer } from '@london-life/contracts';
import { ProfileScreen, type ProfileTabKey } from './profile/profile-screen';
import { contentPublished, useCatalogue } from './lib/content';
import { toPreviewCharacter } from '@london-life/game/appearance';
import { geo } from '@london-life/game/city/layout';
import { areasOnTheWay } from '@london-life/game/journey/path';
import type { RideView } from '@london-life/game/journey/ride';
import { RideScreen } from './journey/ride-screen';
import { useVisits } from './visits/use-visits';
import { NeighboursSheet } from './visits/neighbours-sheet';
import { BillboardSheet } from './adverts/billboard-sheet';
import { mapAdvertsOf } from './adverts/use-adverts';
import { BusinessScreen } from './business/business-screen';
import { BankScreen } from './bank/bank-screen';
import { HousesScreen } from './house/houses-screen';
import { RailCoScreen } from './rail/railco-screen';
import { BetsScreen } from './bets/bets-screen';
import { HealthScreen } from './health/health-screen';
import { CoursesScreen } from './courses/courses-screen';
import { StaffScreen } from './staff/staff-screen';
import { CarsScreen } from './cars/cars-screen';
import { FamilyScreen } from './family/family-screen';
import { HustleScreen } from './hustle/hustle-screen';
import { GistScreen } from './gist/gist-screen';
import { GovScreen } from './gov/gov-screen';
import { policyFromPlay } from './lib/gov';
import type { PhoneAppProps } from './lib/phone-app';
import { ShopsSheet } from './business/shops-sheet';
import { FlightsSheet } from './airport/flights-sheet';
import { FlightScreen } from './airport/flight-screen';
import { TrainsSheet } from './rail/trains-sheet';
import { TrainScreen } from './rail/train-screen';
import { RateTrip } from './rail/rate-trip';
import { BringAlongSheet, SeatOfferAlert } from './airport/bring-along';
import { useAdverts } from './adverts/use-adverts';
import { VisitScreen } from './visits/visit-screen';
import { KnockAlert } from './visits/knock-alert';
import { ActionQueue } from './play/action-queue';
import { api, csrf } from './lib/api';
import { browserAuth } from './lib/auth';
import { actionKey } from './lib/format';

// the phone's full-screen apps: one shape (close, and the wallet changed), one place that picks the screen
type PhoneAppScreen =
  | 'bank'
  | 'bets'
  | 'business'
  | 'cars'
  | 'courses'
  | 'family'
  | 'gist'
  | 'gov'
  | 'health'
  | 'houses'
  | 'hustle'
  | 'railco'
  | 'staff';
const PHONE_APP_SCREENS: Record<PhoneAppScreen, (props: PhoneAppProps) => ReactNode> = {
  bank: BankScreen,
  bets: BetsScreen,
  cars: CarsScreen,
  courses: CoursesScreen,
  family: FamilyScreen,
  hustle: HustleScreen,
  gist: GistScreen,
  gov: GovScreen,
  health: HealthScreen,
  business: BusinessScreen,
  houses: HousesScreen,
  railco: RailCoScreen,
  staff: StaffScreen,
};
function PhoneAppView({ app, ...props }: PhoneAppProps & { app: PhoneAppScreen }) {
  const Screen = PHONE_APP_SCREENS[app];
  return <Screen {...props} />;
}

// answers already given at email sign-up, put back into the form if sending them failed
const valuesFrom = (setup: ProfileSetup): ProfileValues => {
  const [year, month, day] = setup.dateOfBirth.split('-');
  return { username: setup.username, year, month: String(Number(month)), day: String(Number(day)), agreed: true };
};

// the map comes first; sign-in opens over it; a signed-in player finishes their profile before playing
// the ride's views (Lagos Life's chips), and this device's remembered choices
const RIDE_VIEWS: { id: RideView; icon: string; name: string }[] = [
  { id: 'chase', icon: '🎥', name: 'Behind' },
  { id: 'side', icon: '👀', name: 'Side' },
  { id: 'inside', icon: '💺', name: 'My seat' },
];
const TRIP_KEY = 'll.trip.view';
const RIDE_KEY = 'll.ride.view';
const remembered = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const remember = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // a private window: the choice lasts this visit
  }
};

export function App() {
  const legal = legalDocumentAt(window.location.pathname);
  if (legal) return <LegalPage document={legal} />;
  return <Game />;
}

// every place on the map, by id, for the venue bar

function Game() {
  const consent = useConsent();
  const session = useSession(api, browserAuth, 'player', csrf);
  const [signingIn, setSigningIn] = useState(false);
  const [pending, setPending] = useState<{ values: ProfileValues; error: FriendlyError } | null>(null);
  const signedIn = session.state.status === 'signed-in' ? session.state : null;
  const held = session.state.status === 'held' ? session.state.hold : null;
  const signOut = () => void session.signOut();
  const ready = signedIn?.profile.status === 'complete';
  // playing needs a verified email (it protects starting money from throwaway accounts)
  const verified = Boolean(signedIn?.user.emailVerified);
  const [accountOpen, setAccountOpen] = useState(false);
  // the player's character and money; a player with a finished profile and no life is offered the creator
  const [life, setLife] = useState<MyLifeReply | null>(null);
  const [catalogue, setCatalogue] = useState<CharacterOptionsReply | null>(null);
  const [creating, setCreating] = useState(false);
  // the player's house: opened straight after the character is made, and from Home on the map
  const [house, setHouse] = useState<HouseOpen | null>(null);
  useEffect(() => {
    if (!ready) return setLife(null);
    let live = true;
    void api.life.mine().then((reply) => {
      if (!live) return;
      setLife(reply);
      if (!reply.life) setCreating(true);
    });
    return () => {
      live = false;
    };
  }, [ready]);
  useEffect(() => {
    if (creating && !catalogue) void api.characterOptions().then(setCatalogue);
  }, [creating, catalogue]);
  // the game loop, once the player has a character
  const play = usePlay(Boolean(life?.life));
  const [atPlace, setAtPlace] = useState(false);
  // on the way somewhere: the map stays up with a pill until the journey ends, then the place opens (as in
  // Lagos Life)
  const [arriving, setArriving] = useState<{
    id: string;
    name: string;
    mode: TravelMode;
    // where from, and when the journey started and ends (the map shows it)
    from: string;
    startedAt: number;
    endsAt: number;
    // where the player is when it ends, if not the place itself (a neighbour's door, or inside their home)
    lands?: string;
  } | null>(null);
  // on the way: the street (Lagos Life's ride scene) or the map, and which way to watch the ride; both kept on
  // this device. Walking shows the map unless the player asks for the street.
  const [tripView, setTripView] = useState<'ride' | 'map'>(() => (remembered(TRIP_KEY) === 'map' ? 'map' : 'ride'));
  const [rideView, setRideView] = useState<RideView>(() => {
    const saved = remembered(RIDE_KEY);
    return saved === 'side' || saved === 'inside' ? saved : 'chase';
  });
  const [walkStreet, setWalkStreet] = useState(false);
  const [rideUnavailable, setRideUnavailable] = useState(false);
  // other players where the character is
  const people = usePeople(Boolean(life?.life), play.state?.placeId ?? null, () => void play.refresh(), atPlace);
  // chat: one live line while there is a character; the phone shows the chats, a card opens one
  const realtime = useRealtime(Boolean(life?.life));
  // visits (as in Lagos Life): the one you are on, and who is at your door or inside; the Neighbours list
  const visits = useVisits(Boolean(life?.life), realtime, () => void play.refresh());
  const [neighbours, setNeighbours] = useState<{ open: boolean; host: string | null }>({ open: false, host: null });
  // the map's advert boards: what is on them, and the one tapped
  const adverts = useAdverts();
  const [board, setBoard] = useState<number | null>(null);
  // the Business app (as Lagos Life's Company)
  // the phone app open over the map (each takes the whole screen; one at a time)
  const [phoneApp, setPhoneApp] = useState<PhoneAppScreen | null>(null);
  // a phone app asked for a place's card on the map (Health: go to the clinic)
  const [placeRequest, setPlaceRequest] = useState<{ placeId: string; tick: number } | null>(null);
  // a ride on a player's train just ended: stars for the company first, then the place
  const [rating, setRating] = useState<{ trip: FlightView; then: () => void } | null>(null);
  // players' shops: open, and the shop to show first (from a chat's shop card)
  const [shopsOpen, setShopsOpen] = useState<{ open: boolean; focus: string | null }>({ open: false, focus: null });
  // Departures at an airport (from its desk or its pin on the map), and a flight under way
  const [departures, setDepartures] = useState<{ placeId: string; name: string; terminal?: () => void } | null>(null);
  // the ticket office at King's Cross (its pin on the map, or the desk inside)
  const [tickets, setTickets] = useState<{ placeId: string; name: string; terminal?: () => void } | null>(null);
  // players' trains (Rail Co.) in the ticket office, fetched each time it opens
  const [railOffers, setRailOffers] = useState<PlayerTrainOffer[]>([]);
  const ticketsOpen = Boolean(tickets);
  useEffect(() => {
    if (!ticketsOpen) return;
    let live = true;
    api.railco
      .offers()
      .then((reply) => live && setRailOffers(reply.offers))
      .catch(() => live && setRailOffers([]));
    return () => {
      live = false;
    };
  }, [ticketsOpen]);
  const [flying, setFlying] = useState<FlightView | null>(null);
  // "Bring someone along": the sheet to offer seats, and the seats friends saved for me
  const [bringOpen, setBringOpen] = useState(false);
  const [seatOffers, setSeatOffers] = useState<SeatOffer[]>([]);
  const [seatBusy, setSeatBusy] = useState(false);
  const [chat, setChat] = useState<{ open: boolean; target: ChatOpen | null }>({ open: false, target: null });
  const [unread, setUnread] = useState(0);
  // the profile screen (a returning player's character, needs and settings)
  const [profileTab, setProfileTab] = useState<ProfileTabKey | null>(null);
  const placeConversation = useRef<string | null>(null);
  const rememberPlaceConversation = useCallback((id: string | null) => {
    placeConversation.current = id;
  }, []);
  const openChatWith = (lifeId: string) => {
    setProfileTab(null);
    setAtPlace(false);
    setChat({ open: true, target: { kind: 'direct', lifeId } });
  };
  // the Phone's badge: unread chats and groups (message requests never add to it)
  const countUnread = useCallback(() => {
    void Promise.all([api.chat.list(), api.chat.team().catch(() => null)])
      .then(([reply, team]) =>
        setUnread([...reply.chats, ...reply.groups].reduce((sum, item) => sum + item.unread, 0) + (team?.unread ?? 0)),
      )
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (life?.life) countUnread();
  }, [life?.life, realtime.generation, countUnread]);
  // the bell: notices waiting (message requests and the like)
  const [bellUnread, setBellUnread] = useState(0);
  const [noticesOpen, setNoticesOpen] = useState(false);
  const countNotices = useCallback(() => {
    void api
      .notices()
      .then((reply) => setBellUnread(reply.unread))
      .catch(() => {});
  }, [setBellUnread]);
  useEffect(() => {
    if (life?.life) countNotices();
  }, [life?.life, realtime.generation, countNotices]);
  // Gist's own badge: its notifications not seen yet (as Lagos, on the app, not the bell)
  const [gistUnread, setGistUnread] = useState(0);
  const countGist = useCallback(() => {
    void api.gist
      .unread()
      .then((reply) => setGistUnread(reply.unread))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (life?.life) countGist();
  }, [life?.life, realtime.generation, countGist]);
  // a shared Gist link (?gist=<post>): Gist opens on that post
  const [gistLink, setGistLink] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!life?.life) return;
    const id = new URLSearchParams(window.location.search).get('gist');
    if (!id) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (!/^[0-9a-f-]{36}$/.test(id)) return;
    setGistLink(id);
    setPhoneApp('gist');
  }, [life?.life]);
  // Gov opened on the ballot: from City Hall's booths ('ballot'), or a ballot link (?vote=<username>) with that
  // candidate ready
  const [govOpen, setGovOpen] = useState<string | undefined>(undefined);
  const openGov = useCallback((at?: string) => {
    setGovOpen(at);
    setPhoneApp('gov');
  }, []);
  useEffect(() => {
    if (!life?.life) return;
    const name = new URLSearchParams(window.location.search).get('vote');
    if (!name) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (!/^[A-Za-z0-9_]{3,20}$/.test(name)) return;
    openGov(name);
  }, [life?.life, openGov]);
  // prices and pay on the screens follow the Mayor's policy in force (the player's state names it)
  useEffect(() => policyFromPlay(play.state?.policy), [play.state?.policy]);
  // a new message while you are elsewhere: a snackbar (and a chime, if sounds are on); tapping it opens the chat
  const soundsOn = useRef(true);
  // money sent to you or a request paid: the wallet shows it at once
  const refreshWallet = useRef(play.refresh);
  refreshWallet.current = play.refresh;
  const showing = useRef<ChatOpen | null>(null);
  showing.current = chat.open ? chat.target : null;
  useEffect(() => {
    let timer = 0;
    const stop = realtime.listen((event) => {
      // a team post reaches everyone at once: spread the recounts over 20 seconds so the API is not hit together
      if (event.type === 'team' && !chat.open) {
        window.clearTimeout(timer);
        timer = window.setTimeout(countUnread, 800 + Math.random() * 20_000);
      }
      if (event.type === 'notice') countNotices();
      // the admin published new places or activities: load them
      if (event.type === 'content') contentPublished(event.version);
      if (event.type !== 'message' || event.message.mine || event.message.sender.userId === signedIn?.user.id) return;
      if (event.message.kind === 'money' || event.message.kind === 'gift') void refreshWallet.current();
      if (event.message.conversationId === placeConversation.current) return;
      // a message while the chats are closed: count again (shortly, so a burst is one request)
      if (!chat.open) {
        window.clearTimeout(timer);
        timer = window.setTimeout(countUnread, 800);
      }
      // a stranger's message (a request) is quiet: it waits under the bell
      if (event.request || event.message.kind === 'system') return;
      const open = showing.current;
      const looking =
        (open?.kind === 'direct' && !event.group && open.lifeId === event.message.sender.lifeId) ||
        (open?.kind === 'group' && open.conversationId === event.message.conversationId);
      if (looking) return;
      // the game open in a tab you are not looking at: a phone-style alert, if you allowed them
      if (document.visibilityState === 'hidden') {
        const sender = event.message.sender;
        void showLocalAlert(
          event.group ? `${sender.firstName} in ${event.group.name}` : sender.firstName,
          popupText(event.message),
          event.group ? `/?chat=group:${event.message.conversationId}` : `/?chat=direct:${sender.lifeId}`,
          `chat:${event.message.conversationId}`,
        ).catch(() => {});
      }
      const message: ChatMessage = event.message;
      const target: ChatOpen = event.group
        ? { kind: 'group', conversationId: message.conversationId }
        : { kind: 'direct', lifeId: message.sender.lifeId };
      showMessageToast(message, event.group?.name ?? null, () => {
        setProfileTab(null);
        setAtPlace(false);
        setChat({ open: true, target });
      });
      if (soundsOn.current) playChime();
    });
    return () => {
      stop();
      window.clearTimeout(timer);
    };
  }, [realtime, chat.open, countUnread, countNotices, signedIn?.user.id]);
  // an alert tapped on the phone opens its chat (in the address, or sent by the service worker)
  const openFromLink = useCallback((href: string) => {
    const chatLink = new URL(href, window.location.origin).searchParams.get('chat');
    const [kind, id] = chatLink?.split(':') ?? [];
    if (!id || !/^[0-9a-f-]{36}$/.test(id)) return;
    setChat({
      open: true,
      target: kind === 'group' ? { kind: 'group', conversationId: id } : { kind: 'direct', lifeId: id },
    });
  }, []);
  useEffect(() => {
    if (!life?.life) return;
    void refreshPush().catch(() => {});
    if (new URLSearchParams(window.location.search).has('chat')) {
      openFromLink(window.location.href);
      window.history.replaceState(null, '', window.location.pathname);
    }
    const onWorker = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | null;
      if (data?.type === 'open' && data.url) openFromLink(data.url);
    };
    navigator.serviceWorker?.addEventListener('message', onWorker);
    return () => navigator.serviceWorker?.removeEventListener('message', onWorker);
  }, [life?.life, openFromLink]);
  const content = useCatalogue();
  // the player on the map during a journey: their character and @name
  const living = life?.life ?? null;
  const username = signedIn?.profile.username ?? null;
  const traveller = useMemo(
    () =>
      living
        ? {
            character: toPreviewCharacter(living.appearance, living.firstName),
            tag: username ? `@${username}` : living.firstName,
          }
        : null,
    [living, username],
  );
  // the street on the way, unless the player chose the map (walking: unless they asked for the street) or the
  // device cannot draw it
  const showRide = !!arriving && !rideUnavailable && (arriving.mode === 'walk' ? walkStreet : tripView === 'ride');
  // the player's own car, for a journey by car (the ride draws it in its paint)
  const ownCar = play.state?.car ?? null;
  // the areas the way passes through, for its "Now passing" signs
  const rideAreas = useMemo(() => {
    if (!arriving || !content) return [];
    const spot = (id: string) => {
      const found = content.place(id);
      return found ? { area: found.area, position: geo(found.lon, found.lat) } : null;
    };
    const from = spot(arriving.from);
    const to = spot(arriving.id);
    if (!from || !to) return to ? [to.area] : [];
    const places = content.places
      .filter((item) => item.visible && item.id !== HOME_PLACE)
      .map((item) => ({ area: item.area, position: geo(item.lon, item.lat) }));
    return areasOnTheWay(from, to, places);
  }, [arriving, content]);
  // while riding, the screen is the street: the map's shortcuts and controls step aside (as in Lagos Life)
  useEffect(() => {
    document.body.classList.toggle('riding', showRide || Boolean(flying));
    return () => document.body.classList.remove('riding');
  }, [showRide, flying]);
  const place = play.state ? content?.place(play.state.placeId) : undefined;
  // what the game says back (and what went wrong away from a place) goes out as a snackbar
  useEffect(() => {
    if (play.message) notify(play.message);
    if (people.message) notify(people.message);
    if (play.message || people.message) {
      play.clearMessage();
      people.clearMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play.message, people.message]);
  useEffect(() => {
    if (atPlace || !play.failure) return;
    notifyProblem(play.failure.message);
    play.clearFailure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play.failure, atPlace]);
  const start = () => (signedIn ? ready && !life?.life && setCreating(true) : setSigningIn(true));
  const [settings, setSettings] = useState<PlayerSettings>(DEFAULT_PLAYER_SETTINGS);
  soundsOn.current = settings.sounds;
  // coming back from Messages opens the phone again
  const [phoneOpenTick, setPhoneOpenTick] = useState(0);
  const openProfile = (tab: ProfileTabKey) => setProfileTab(tab);
  const phoneApps: PhoneApp[] | undefined = life?.life
    ? [
        {
          id: 'messages',
          label: 'Messages',
          icon: '💬',
          tone: 'green',
          badge: unread,
          onOpen: () => setChat({ open: true, target: null }),
        },
        { id: 'people', label: 'People', icon: '👥', tone: 'blue', onOpen: () => openProfile('people') },
        ...(content?.family?.visible !== false
          ? [
              {
                id: 'family',
                label: 'Family',
                icon: '💞',
                tone: 'purple' as const,
                onOpen: () => setPhoneApp('family'),
              },
            ]
          : []),
        { id: 'home', label: 'Home', icon: '🏠', tone: 'orange', onOpen: () => setHouse('open') },
        { id: 'houses', label: 'Houses', icon: '🔑', tone: 'orange', onOpen: () => setPhoneApp('houses') },
        ...(content?.staff?.visible !== false
          ? [{ id: 'staff', label: 'Staff', icon: '🧹', tone: 'teal' as const, onOpen: () => setPhoneApp('staff') }]
          : []),
        ...(content?.cars?.visible !== false
          ? [{ id: 'cars', label: 'Cars', icon: '🚗', tone: 'red' as const, onOpen: () => setPhoneApp('cars') }]
          : []),
        { id: 'shop', label: 'Shop', icon: '🛋️', tone: 'gold', onOpen: () => setHouse('shop') },
        { id: 'jobs', label: 'Jobs', icon: '💼', tone: 'navy', onOpen: () => openProfile('career') },
        ...(content?.hustle?.visible !== false
          ? [
              {
                id: 'hustle',
                label: 'Hustle',
                icon: '🧰',
                tone: 'orange' as const,
                onOpen: () => setPhoneApp('hustle'),
              },
            ]
          : []),
        ...(content?.gist?.visible !== false
          ? [
              {
                id: 'gist',
                label: 'Gist',
                icon: '📰',
                tone: 'navy' as const,
                badge: gistUnread,
                onOpen: () => setPhoneApp('gist'),
              },
            ]
          : []),
        ...(content?.gov?.visible !== false
          ? [{ id: 'gov', label: 'Gov', icon: '🏛️', tone: 'teal' as const, onOpen: () => openGov() }]
          : []),
        { id: 'bank', label: 'Bank', icon: '🏦', tone: 'gold', onOpen: () => setPhoneApp('bank') },
        { id: 'business', label: 'Business', icon: '🏢', tone: 'teal', onOpen: () => setPhoneApp('business') },
        { id: 'railco', label: 'Rail Co.', icon: '🚆', tone: 'red', onOpen: () => setPhoneApp('railco') },
        ...(content?.betting?.visible !== false
          ? [{ id: 'bets', label: 'Bets', icon: '⚽', tone: 'green' as const, onOpen: () => setPhoneApp('bets') }]
          : []),
        { id: 'goals', label: 'Goals', icon: '🎯', tone: 'purple', onOpen: () => openProfile('goals') },
        { id: 'skills', label: 'Skills', icon: '📚', tone: 'teal', onOpen: () => openProfile('skills') },
        ...(content?.courses?.visible !== false
          ? [
              {
                id: 'courses',
                label: 'Courses',
                icon: '🎓',
                tone: 'navy' as const,
                onOpen: () => setPhoneApp('courses'),
              },
            ]
          : []),
        content?.health?.visible !== false
          ? { id: 'health', label: 'Health', icon: '💊', tone: 'red', onOpen: () => setPhoneApp('health') }
          : { id: 'needs', label: 'Health', icon: '❤️', tone: 'red', onOpen: () => openProfile('needs') },
        { id: 'profile', label: 'Profile', icon: '🪪', tone: 'blue', onOpen: () => openProfile('profile') },
        { id: 'settings', label: 'Settings', icon: '⚙️', tone: 'navy', onOpen: () => openProfile('settings') },
      ]
    : undefined;
  const hasLife = Boolean(life?.life);
  // the phone's back gesture (or a swipe in from the edge) closes the screen on top
  const closeChat = () => {
    setChat({ open: false, target: null });
    setPhoneOpenTick((tick) => tick + 1);
  };
  useBackLayer(Boolean(house), () => setHouse(null));
  useBackLayer(Boolean(profileTab), () => setProfileTab(null));
  useBackLayer(chat.open, closeChat);
  useBackLayer(chat.open && chat.target !== null, () => setChat({ open: true, target: null }));
  useBackLayer(noticesOpen, () => setNoticesOpen(false));
  useBackLayer(atPlace, () => setAtPlace(false));
  useBackLayer(Boolean(phoneApp), () => setPhoneApp(null));
  // the journey is over: open the place (or the house; a visit shows itself)
  useEffect(() => {
    if (!arriving || play.busyFor > 0 || play.state?.placeId !== (arriving.lands ?? arriving.id)) return;
    setArriving(null);
    if (arriving.lands) return;
    if (arriving.id === HOME_PLACE) setHouse('open');
    else setAtPlace(true);
  }, [arriving, play.busyFor, play.state?.placeId]);
  // a flight lands: the player is at the other airport's terminal (as Lagos Life's welcome), and the flight is
  // settled (a lost bag is told in the bell)
  useEffect(() => {
    if (!flying || play.busyFor > 0 || play.state?.placeId !== flying.to.placeId) return;
    const landed = flying;
    setFlying(null);
    notify(
      landed.mode === 'train'
        ? `🚉 ${landed.to.name}. Mind the gap as you leave the train`
        : landed.mode === 'chopper'
          ? `🚁 Touched down at ${landed.to.name}. Straight to the door`
          : landed.mode === 'jet'
            ? `🛬 Welcome to ${landed.to.name}! Your jet is on the private apron`
            : `🛬 Welcome to ${landed.to.name}! Smooth landing`,
    );
    const open = () => (landed.to.placeId === HOME_PLACE ? setHouse('open') : setAtPlace(true));
    if (landed.mode === 'train' && landed.companyId) setRating({ trip: landed, then: open });
    else open();
    void api.play
      .flight()
      .then((reply) => {
        if (reply.flight?.id === landed.id && reply.flight.bagLost)
          notifyProblem(
            landed.coverPence
              ? `🧳 A bag went missing, but your ${landed.mode === 'train' ? 'insurance' : 'cover'} paid`
              : '🧳 Your bag went missing',
          );
      })
      .catch(() => {});
  }, [flying, play.busyFor, play.state?.placeId]);
  // back in the game during a flight (a reload, another device): the flight carries on
  const busyUntil = play.state?.busyUntil ?? null;
  useEffect(() => {
    if (!busyUntil || flying) return;
    let live = true;
    void api.play
      .flight()
      .then((reply) => {
        const flight = reply.flight;
        if (live && flight && Date.parse(flight.landsAt) > Date.now() && flight.landsAt === busyUntil)
          setFlying(flight);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyUntil]);
  // seats friends saved for me, and my flight while people are deciding: looked at again when the server says
  // something changed, and every so often
  const loadSeatOffers = useCallback(() => {
    void api.play
      .seatOffers()
      // the same seats as before: nothing to draw again
      .then((reply) =>
        setSeatOffers((current) => (JSON.stringify(current) === JSON.stringify(reply.offers) ? current : reply.offers)),
      )
      .catch(() => {});
  }, []);
  const flyingId = flying?.id ?? null;
  const refreshFlight = useCallback(() => {
    void api.play
      .flight()
      .then((reply) => {
        if (!reply.flight) return;
        setFlying((current) => (current && current.id === reply.flight!.id ? reply.flight : current));
        void play.refresh();
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // once a character is there, and every so often
  useEffect(() => {
    if (!hasLife) return;
    loadSeatOffers();
    const timer = window.setInterval(loadSeatOffers, 30_000);
    return () => window.clearInterval(timer);
  }, [hasLife, loadSeatOffers]);
  // and when the server says a seat was offered or answered (listening only: this runs on every new listener)
  const flyingRef = useRef(flyingId);
  flyingRef.current = flyingId;
  useEffect(() => {
    if (!hasLife) return;
    return realtime.listen((event) => {
      if (event.type !== 'flight') return;
      loadSeatOffers();
      if (flyingRef.current) refreshFlight();
    });
    // the listener is added once per connection, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLife, realtime.generation, loadSeatOffers, refreshFlight]);
  const deciding = Boolean(flying && flying.waiting > 0);
  useEffect(() => {
    if (!deciding) return;
    const timer = window.setInterval(refreshFlight, 5_000);
    return () => window.clearInterval(timer);
  }, [deciding, refreshFlight]);
  const offerSeats = async (lifeIds: string[]) => {
    setSeatBusy(true);
    try {
      const reply = await api.play.offerSeats({ lifeIds }, actionKey('seats'));
      setFlying(reply.flight);
      setBringOpen(false);
      if (reply.message) notify(reply.message);
      void play.refresh();
    } catch (error) {
      notifyProblem(describeError(error).message);
    } finally {
      setSeatBusy(false);
    }
  };
  const answerSeat = async (offer: SeatOffer, take: boolean) => {
    setSeatBusy(true);
    try {
      if (!take) {
        setSeatOffers((await api.play.declineSeat(offer.id)).offers);
        return;
      }
      const reply = await api.play.takeSeat(offer.id, actionKey('seat'));
      setSeatOffers((list) => list.filter((item) => item.id !== offer.id));
      closeScreens();
      setArriving(null);
      setFlying(reply.flight);
      if (reply.message) notify(reply.message);
      void play.refresh();
    } catch (error) {
      notifyProblem(describeError(error).message);
      loadSeatOffers();
    } finally {
      setSeatBusy(false);
    }
  };
  // set off to a neighbour's home: the same journey as anywhere (the ride, the pill, Skip), ending at their door
  const visitHome = async (host: NeighbourView, mode: TravelMode) => {
    const from = mapPlaceOf(play.state?.placeId ?? HOME_PLACE);
    const reply = await visits.start(host.lifeId, mode);
    if (!reply?.visit) return false;
    const state = await play.refresh().then(() => play.latest());
    // the journey's times, from the server: it ends when the player is free, and lasts as long as the way takes
    const endsAt = new Date(state?.busyUntil ?? reply.visit.arrivesAt).getTime();
    const seconds = mode === 'car' ? (state?.car?.seconds ?? 0) : (content?.travelMode(mode)?.seconds ?? 0);
    const startedAt = endsAt - seconds * 1000;
    closeScreens();
    setRideUnavailable(false);
    setWalkStreet(false);
    setArriving({
      id: HOME_PLACE,
      name: `${host.username ? `@${host.username}` : host.firstName}'s`,
      mode,
      from,
      startedAt,
      endsAt,
      lands: state?.placeId,
    });
    return true;
  };
  // what the character is doing (Lagos Life's chip: the emoji in a filling ring, "× tap to cancel"), over the
  // screen that is open, or on the map
  const actionQueue = (onScreen: boolean) => (
    <ActionQueue
      state={play.state}
      busyFor={play.busyFor}
      queue={play.queue}
      onCancel={play.cancel}
      className={onScreen ? 'on-screen' : ''}
    />
  );
  // on a visit (the journey over): at their door, or inside
  const visiting = hostOf(play.state?.placeId);
  const myVisit = visits.reply?.visit && visits.reply.visit.status !== 'ended' ? visits.reply.visit : null;
  // a place, the home or a visit is open over the map: the chip shows over it, not on the map behind
  const screenOpen = atPlace || Boolean(house) || Boolean(visiting && myVisit);
  const visitRevision = useMemo(
    () => JSON.stringify([myVisit?.status, myVisit?.host.atHome, visits.reply?.guests.map((guest) => guest.visitId)]),
    [myVisit, visits.reply],
  );
  // the dock stays on every screen; its buttons first close whatever screen is open
  const closeScreens = () => {
    setHouse(null);
    setProfileTab(null);
    setChat({ open: false, target: null });
    setNoticesOpen(false);
    setAtPlace(false);
    setPhoneApp(null);
  };
  // off: by air, in my own jet or the VIP chopper (the server decides the fare and the times)
  const takeOff = (body: FlyRequest) =>
    void play.fly(body).then((flight) => {
      if (!flight) return;
      setDepartures(null);
      closeScreens();
      setArriving(null);
      setFlying(flight);
    });
  const dockActive =
    profileTab || chat.open || noticesOpen || phoneApp ? 'phone' : house === 'shop' ? 'buy' : house ? 'home' : null;
  useEffect(() => {
    if (!hasLife) return;
    void api.account
      .settings()
      .then((reply) => setSettings({ ...DEFAULT_PLAYER_SETTINGS, ...reply.settings }))
      .catch(() => {});
  }, [hasLife]);
  // warnings from the moderators, one at a time
  const [warnings, setWarnings] = useState<PlayerWarning[]>([]);
  useEffect(() => {
    if (!ready) return setWarnings([]);
    void api
      .warnings()
      .then((reply) => setWarnings(reply.warnings))
      .catch(() => {});
  }, [ready]);
  const deleteAccount = async (confirmUsername: string) => {
    await api.deleteAccount({ confirmUsername });
    setAccountOpen(false);
    setProfileTab(null);
    await session.signOut();
  };

  const setUpProfile = async (body: ProfileSetup) => {
    await api.profile.setUp(body);
    setPending(null);
    await session.refresh();
  };
  // creating an account with email answers the profile questions in the same form; if sending them
  // fails (say the name was taken a moment ago) the profile card opens with the answers and the reason
  const signUp = async (email: string, password: string, name: string, profile: ProfileSetup) => {
    await session.signUpWithEmail(email, password, name);
    try {
      await api.profile.setUp(profile);
    } catch (error) {
      setPending({ values: valuesFrom(profile), error: describeError(error) });
    }
    await session.refresh();
  };

  return (
    <>
      <PublicHomepage
        returning={Boolean(life?.life)}
        walletPence={play.state?.walletPence ?? life?.life?.walletPence}
        dataSaver={settings.dataSaver}
        onProfile={hasLife ? setProfileTab : undefined}
        account={
          signedIn ? (
            <div className="account-pill" aria-label="Your account">
              {signedIn.profile.username && <span>@{signedIn.profile.username}</span>}
              {life?.life && <span>{life.life.firstName}</span>}
              <button
                type="button"
                className="account-open"
                onClick={() => (hasLife ? setProfileTab('settings') : setAccountOpen(true))}
              >
                Account
              </button>
              <button type="button" onClick={signOut}>
                Sign out
              </button>
            </div>
          ) : undefined
        }
        play={
          play.state && play.needs
            ? { placeId: mapPlaceOf(play.state.placeId), needs: play.needs, car: play.state.car }
            : null
        }
        onNeighbours={hasLife ? () => setNeighbours({ open: true, host: null }) : undefined}
        onShops={hasLife ? () => setShopsOpen({ open: true, focus: null }) : undefined}
        onGov={hasLife ? () => openGov() : undefined}
        adverts={mapAdvertsOf(adverts.reply)}
        placeRequest={placeRequest}
        onBoard={setBoard}
        actionQueue={hasLife && !arriving && !screenOpen ? actionQueue(false) : undefined}
        onStart={() => (life?.life ? setHouse('open') : start())}
        onShop={life?.life ? () => setHouse('shop') : undefined}
        phoneApps={phoneApps}
        phoneBadge={unread}
        phoneOpenTick={phoneOpenTick}
        onDock={hasLife ? closeScreens : undefined}
        journey={arriving ? { ...arriving, to: arriving.id } : null}
        traveller={traveller}
        dockActive={dockActive}
        bell={life?.life ? { unread: bellUnread, onOpen: () => setNoticesOpen(true) } : undefined}
        onDepartures={
          hasLife ? (target, terminal) => setDepartures({ placeId: target.id, name: target.name, terminal }) : undefined
        }
        onTickets={
          hasLife ? (target, terminal) => setTickets({ placeId: target.id, name: target.name, terminal }) : undefined
        }
        onGo={(target, mode) => {
          // the home on the map is the house itself
          if (play.state?.placeId === target.id) return target.id === HOME_PLACE ? setHouse('open') : setAtPlace(true);
          const from = mapPlaceOf(play.state?.placeId ?? HOME_PLACE);
          const startedAt = Date.now();
          void play.travel(target.id, mode).then((ok) => {
            if (!ok) return;
            const endsAt = play.latest()?.busyUntil;
            setRideUnavailable(false);
            setWalkStreet(false);
            setArriving({
              id: target.id,
              name: target.name,
              mode,
              from,
              startedAt,
              endsAt: endsAt ? new Date(endsAt).getTime() : startedAt,
            });
          });
        }}
      />
      {arriving && showRide && traveller && (
        <RideScreen
          journey={{
            ...arriving,
            to: arriving.id,
            icon: content?.place(arriving.id)?.icon ?? '📍',
            car: ownCar ? { paint: ownCar.colour, bike: ownCar.bike, driver: ownCar.driver } : undefined,
          }}
          areas={rideAreas}
          character={traveller.character}
          night={isLondonNight()}
          dataSaver={settings.dataSaver}
          view={rideView}
          onUnavailable={() => setRideUnavailable(true)}
        />
      )}
      {arriving && (
        <div className={showRide ? 'on-the-way riding' : 'on-the-way'}>
          <div className="on-the-way-pill" role="status">
            <span aria-hidden="true">
              {arriving.mode === 'car' ? (ownCar?.emoji ?? '🚗') : (content?.travelMode(arriving.mode)?.icon ?? '🚶')}
            </span>{' '}
            On the way to {arriving.name}…
          </div>
          <div className="on-the-way-chips">
            {showRide && (
              <div className="ride-views" role="group" aria-label="How to watch">
                {RIDE_VIEWS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={item.name}
                    title={item.name}
                    aria-pressed={rideView === item.id}
                    onClick={() => {
                      setRideView(item.id);
                      remember(RIDE_KEY, item.id);
                    }}
                  >
                    {item.icon}
                  </button>
                ))}
              </div>
            )}
            {!rideUnavailable && (
              <button
                type="button"
                onClick={() => {
                  if (arriving.mode === 'walk') return setWalkStreet(!showRide);
                  const next = showRide ? 'map' : 'ride';
                  setTripView(next);
                  remember(TRIP_KEY, next);
                }}
              >
                {showRide ? '🗺️ Map' : arriving.mode === 'walk' ? '👀 Street' : '🚗 Ride'}
              </button>
            )}
            {play.busyFor > 2 && (
              <button type="button" onClick={() => void play.skip()}>
                ⏭ Skip
              </button>
            )}
          </div>
        </div>
      )}
      {visiting && myVisit && !arriving && play.busyFor === 0 && life?.life && !house && !chat.open && !profileTab && (
        <VisitScreen
          visit={myVisit}
          inside={visiting.inside}
          life={life.life}
          myTag={username ? `@${username}` : life.life.firstName}
          dataSaver={settings.dataSaver}
          busy={visits.busy || people.busy}
          onLeave={() => void visits.leave()}
          onChat={openChatWith}
          onTogether={people.act}
          revision={visitRevision}
        />
      )}
      {atPlace && place && play.state && play.needs && life?.life && (
        <VenueScreen
          place={place}
          activities={content?.activitiesAt(place.id) ?? []}
          life={life.life}
          dataSaver={settings.dataSaver}
          onHome={() => {
            setAtPlace(false);
            if (play.state?.placeId === HOME_PLACE) return setHouse('open');
            void play.travel(HOME_PLACE, 'walk').then((ok) => ok && setHouse('open'));
          }}
          needs={play.needs}
          walletPence={play.state.walletPence}
          busyLabel={play.state.busyLabel}
          busyFor={play.busyFor}
          sending={play.sending}
          failure={play.failure}
          onDo={(activityId) => void play.act(activityId)}
          onOpen={(what) =>
            what === 'flights'
              ? setDepartures({ placeId: place.id, name: place.name })
              : what === 'ballot'
                ? openGov('ballot')
                : setTickets({ placeId: place.id, name: place.name })
          }
          shifts={play.state.progress.jobs}
          coursesDone={play.state.progress.courses}
          onClose={() => setAtPlace(false)}
          people={people.reply?.here ?? []}
          peopleBusy={people.busy}
          onPerson={people.act}
          onChat={openChatWith}
          realtime={realtime}
          onPlaceConversation={rememberPlaceConversation}
          myUserId={signedIn?.user.id ?? null}
          myUsername={signedIn?.profile.username ?? null}
        />
      )}
      {signingIn && !signedIn && (
        <div
          className="sign-in-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) setSigningIn(false);
          }}
        >
          <SignInCard
            title="Sign in to London Life"
            onGoogle={session.signInWithGoogle}
            onEmailSignIn={session.signInWithEmail}
            onEmailSignUp={signUp}
            checkAvailability={api.profile.usernameAvailability}
            onReset={session.sendPasswordReset}
          />
        </div>
      )}
      {signedIn?.profile.status === 'incomplete' && (
        <div className="sign-in-overlay">
          <CompleteProfileCard
            username={signedIn.profile.username}
            checkAvailability={api.profile.usernameAvailability}
            onSubmit={setUpProfile}
            onSignOut={signOut}
            initial={pending?.values}
            initialError={pending?.error}
          />
        </div>
      )}
      {signedIn && accountOpen && (
        <div className="sign-in-overlay">
          <AccountCard
            username={signedIn.profile.username}
            email={signedIn.user.email}
            characterName={life?.life?.firstName ?? null}
            onClose={() => setAccountOpen(false)}
            onCookieSettings={() => {
              setAccountOpen(false);
              consent.reopen();
            }}
            onSignOut={() => {
              setAccountOpen(false);
              signOut();
            }}
            onDelete={deleteAccount}
          />
        </div>
      )}
      {ready && !verified && !life?.life && !accountOpen && (
        <div className="sign-in-overlay">
          <VerifyEmailCard
            email={signedIn?.user.email ?? null}
            onResend={session.resendVerification}
            onCheck={session.confirmEmailVerified}
            onSignOut={signOut}
          />
        </div>
      )}
      {ready && verified && creating && catalogue && !life?.life && (
        <div className="sign-in-overlay">
          <LifeCreator
            catalogue={catalogue}
            onClose={() => setCreating(false)}
            onStarted={(reply) => {
              setLife(reply);
              setCreating(false);
              if (reply.life) {
                notify(
                  `Welcome to London, ${reply.life.firstName}. ${formatPence(reply.life.walletPence)} is in your wallet.`,
                  { duration: 7000 },
                );
                setHouse('welcome');
              }
            }}
          />
        </div>
      )}
      {held && (
        <div className="sign-in-overlay">
          <AccountHeldCard hold={held} onSignOut={signOut} />
        </div>
      )}
      {signedIn && warnings[0] && (
        <div className="sign-in-overlay">
          <WarningCard
            text={warnings[0].text}
            createdAt={warnings[0].createdAt}
            onSeen={() => {
              const [first, ...rest] = warnings;
              setWarnings(rest);
              void api.warningSeen(first!.id).catch(() => {});
            }}
          />
        </div>
      )}
      {signedIn?.profile.status === 'age_restricted' && (
        <div className="sign-in-overlay">
          <AgeRestrictedCard onSignOut={signOut} />
        </div>
      )}
      {signedIn && life?.life && house && !profileTab && (
        <HouseScreen
          life={life.life}
          dataSaver={settings.dataSaver}
          open={house}
          onMode={setHouse}
          onClose={() => setHouse(null)}
          onWallet={() => void play.refresh()}
          play={play.state}
          needs={play.needs}
          busyFor={play.busyFor}
          onAct={play.act}
          onGoHome={() => play.travel(HOME_PLACE, 'walk')}
          guests={visits.reply?.guests ?? []}
          onSendHome={(visitId) => void visits.sendHome(visitId)}
        />
      )}
      {signedIn && life?.life && chat.open && (
        <ChatScreen
          realtime={realtime}
          open={chat.target}
          onOpen={(target) => setChat({ open: true, target })}
          onClose={closeChat}
          onUnread={setUnread}
          myUserId={signedIn?.user.id ?? null}
          onWallet={() => void play.refresh()}
          onGoOver={(hostLifeId) => {
            setChat({ open: false, target: null });
            setNeighbours({ open: true, host: hostLifeId });
          }}
          onOpenShop={(businessId) => {
            setChat({ open: false, target: null });
            setShopsOpen({ open: true, focus: businessId });
          }}
        />
      )}
      <BillboardSheet
        board={board}
        onClose={() => setBoard(null)}
        adverts={adverts.reply}
        onChanged={() => {
          void adverts.reload();
          void play.refresh();
        }}
        canBook={Boolean(hasLife)}
        walletPence={play.state?.walletPence ?? life?.life?.walletPence}
        onStart={() => {
          setBoard(null);
          start();
        }}
      />
      {content && departures && (
        <FlightsSheet
          open
          onClose={() => setDepartures(null)}
          airport={content.airport}
          from={departures}
          isOpen={(id) => Boolean(content.place(id)?.visible)}
          atAirport={play.state?.placeId === departures.placeId}
          walletPence={play.state?.walletPence ?? null}
          sending={play.sending}
          onBook={(to, cabin, covered) => takeOff({ from: departures.placeId, to, cabin, covered })}
          places={content.places
            .filter((item) => item.visible)
            .map((item) => ({ id: item.id, name: item.name, icon: item.icon, area: item.area }))}
          onFly={(mode, to) => takeOff({ from: departures.placeId, to, cabin: 'first', covered: false, mode })}
          onWallet={() => void play.refresh()}
          onTerminal={
            departures.terminal
              ? () => {
                  const open = departures.terminal!;
                  setDepartures(null);
                  open();
                }
              : undefined
          }
        />
      )}
      {content && tickets && (
        <TrainsSheet
          open
          onClose={() => setTickets(null)}
          rail={content.rail}
          offers={railOffers}
          railco={content.railco}
          isOpen={(id) => Boolean(content.place(id)?.visible)}
          areaOf={(id) => content.place(id)?.area ?? ''}
          atStation={play.state?.placeId === tickets.placeId}
          walletPence={play.state?.walletPence ?? null}
          sending={play.sending}
          onBook={(ticket) =>
            void play.train(ticket).then((trip) => {
              if (!trip) return;
              setTickets(null);
              closeScreens();
              setArriving(null);
              setFlying(trip);
            })
          }
          onTerminal={
            tickets.terminal
              ? () => {
                  const open = tickets.terminal!;
                  setTickets(null);
                  open();
                }
              : undefined
          }
        />
      )}
      {flying && flying.mode === 'train' && content && traveller && (
        <TrainScreen
          trip={flying}
          rail={content.rail}
          character={traveller.character}
          night={isLondonNight()}
          dataSaver={settings.dataSaver}
          icon={content.place(flying.to.placeId)?.icon ?? '🚉'}
        />
      )}
      {flying && flying.mode !== 'train' && content && (
        <FlightScreen
          flight={flying}
          airport={content.airport}
          night={isLondonNight()}
          dataSaver={settings.dataSaver}
          boardAd={adverts.reply?.onAir.find((advert) => boardOf(advert.board)?.where === 'airport')?.image ?? null}
          owner={traveller?.tag}
          onBringAlong={flying.host || flying.mode !== 'air' ? undefined : () => setBringOpen(true)}
        />
      )}
      {flying && content && (
        <BringAlongSheet
          open={bringOpen}
          onClose={() => setBringOpen(false)}
          destination={flying.to.name}
          seats={bringAlongOf(content.airport).seats}
          holdSeconds={bringAlongOf(content.airport).holdSeconds}
          sending={seatBusy}
          onOffer={(lifeIds) => void offerSeats(lifeIds)}
        />
      )}
      {hasLife && !flying && seatOffers[0] && (
        <SeatOfferAlert
          key={seatOffers[0].id}
          offer={seatOffers[0]}
          walletPence={play.state?.walletPence ?? null}
          busy={seatBusy}
          onAnswer={(take) => void answerSeat(seatOffers[0]!, take)}
        />
      )}
      {signedIn && life?.life && (
        <ShopsSheet
          open={shopsOpen.open}
          focus={shopsOpen.focus}
          onClose={() => setShopsOpen({ open: false, focus: null })}
          onWallet={() => void play.refresh()}
          onLook={() => void api.life.mine().then((reply) => setLife(reply))}
        />
      )}
      {signedIn && life?.life && phoneApp && (
        <PhoneAppView
          app={phoneApp}
          life={life.life}
          dataSaver={settings.dataSaver}
          open={phoneApp === 'gist' ? gistLink : phoneApp === 'gov' ? govOpen : undefined}
          onClose={() => {
            if (phoneApp === 'gist') {
              setGistLink(undefined);
              countGist();
            }
            // opened from City Hall's booths: back to City Hall, not the phone
            const fromBooths = phoneApp === 'gov' && govOpen === 'ballot';
            if (phoneApp === 'gov') setGovOpen(undefined);
            setPhoneApp(null);
            if (!fromBooths) setPhoneOpenTick((tick) => tick + 1);
          }}
          onChat={(lifeId) => {
            setPhoneApp(null);
            openChatWith(lifeId);
          }}
          onWallet={() => void play.refresh()}
          onPlace={(placeId) => {
            setPhoneApp(null);
            setPlaceRequest((current) => ({ placeId, tick: (current?.tick ?? 0) + 1 }));
          }}
          onDo={(activityId, choice) => {
            setPhoneApp(null);
            void play.act(activityId, choice);
          }}
        />
      )}
      {signedIn && rating && (
        <RateTrip
          trip={rating.trip}
          onClose={() => {
            setRating(null);
            rating.then();
          }}
        />
      )}
      {signedIn && life?.life && (
        <NeighboursSheet
          open={neighbours.open}
          hostLifeId={neighbours.host}
          onClose={() => setNeighbours({ open: false, host: null })}
          onVisit={visitHome}
          car={play.state?.car}
        />
      )}
      {hasLife && !arriving && screenOpen && !chat.open && !profileTab && actionQueue(true)}
      {signedIn && life?.life && visits.reply && (
        <KnockAlert
          guests={visits.reply.guests}
          busy={visits.busy}
          onAnswer={(visitId, letIn) => void visits.answer(visitId, letIn)}
        />
      )}
      {signedIn && life?.life && profileTab && (
        <ProfileScreen
          tab={profileTab}
          onTab={setProfileTab}
          life={life.life}
          play={play.state}
          needs={play.needs}
          settings={settings}
          onSettings={setSettings}
          onLife={(updated, message) => {
            setLife({ ...life, life: updated });
            // traits and wallet show on the map straight away
            void play.refresh();
            if (message) notify(message);
          }}
          onMessage={(message) => {
            notify(message);
            void play.refresh();
          }}
          onChat={openChatWith}
          onClose={() => setProfileTab(null)}
          onSignOut={() => {
            setProfileTab(null);
            signOut();
          }}
          onDeleteAccount={deleteAccount}
          onCookieSettings={() => {
            setProfileTab(null);
            consent.reopen();
          }}
          onNewLife={async () => {
            const reply = await api.life.end();
            setLife(reply);
            setProfileTab(null);
            if (!reply.life && !reply.newLifeFrom) setCreating(true);
          }}
        />
      )}
      {signedIn && life?.life && noticesOpen && (
        <NoticesPanel
          onClose={() => setNoticesOpen(false)}
          onRead={() => setBellUnread(0)}
          onOpenChat={(lifeId) => {
            setNoticesOpen(false);
            openChatWith(lifeId);
          }}
        />
      )}
      {consent.showBanner && consent.settings && <CookieBanner settings={consent.settings} onAnswer={consent.answer} />}
      <GameToaster />
    </>
  );
}
